import React, { useCallback } from 'react'
import { styled, useMediaQuery, useTheme } from '@mui/material'
import { TestIds } from '../../../../testIds'
import { Product } from '../../storage'
import { useSelector } from 'react-redux'
import {
  selectCanUseQuickOrder,
  selectCurrentFarmerAccount,
} from '../../../azure-b2c/storage/auth.slice'
import CartSlice from '../../../cart/storage/cart.slice'
import { CartItem } from '../../../cart/storage/cart.types'
import { useAppDispatch } from '../../../../store/rootStore'
import { toast } from 'react-toastify'
import { ItemContainer, ItemLabel } from '../../../../components/styled'
import dayjs from 'dayjs'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import { useQuoteModal } from '../../../quotes/components/QuoteModal/useQuoteModal'
import { isValidDate } from '../../../../util/helpers'
import { useProductStatus } from '../../utils/useProductStatus'
import { useNavigate } from 'react-router-dom'
import QuickOrderButton from './QuickOrderButton'
import { QuoteModalType } from '../../../quotes/components/QuoteModal/types'
import { openProductDetailsLink } from '../ProductDetails/utils/openProductDetails'
import { ProductCardParent } from './ProductCardParent'
import { ProductType } from './types'

export const SideContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '16px',
  [theme.breakpoints.down('sm')]: {
    margin: '0',
  },
}))

export interface ProductCardProps {
  item: Product | CartItem
  disableLink?: boolean
  isPastProduct?: boolean
  isAlternativeProduct?: boolean
  showRemove?: boolean
  enableQuote?: boolean
  condensed?: boolean
  testId?: string
}

export const ProductCard = ({
  item,
  disableLink,
  isPastProduct,
  isAlternativeProduct,
  showRemove,
  enableQuote,
  condensed,
  testId,
}: ProductCardProps) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const quoteProduct = useQuoteModal()
  const canUseQuickOrder = useSelector(selectCanUseQuickOrder)

  const lastOrderedDate = isPastProduct
    ? new Date(Math.max(...item.orders.map((x) => new Date(x.createdAt ?? Date.now()).getTime())))
    : undefined

  const dispatch = useAppDispatch()
  const currentAccount = useSelector(selectCurrentFarmerAccount)

  const { isDisabled, isEndOfLife } = useProductStatus(item.lifeCycleState, item.basePriceExcGst)

  const isAllProduct = React.useMemo(
    () => !isPastProduct && !isAlternativeProduct,
    [isAlternativeProduct, isPastProduct],
  )

  const productType = React.useMemo(() => {
    if (isAlternativeProduct) return ProductType.Alternative
    else if (isPastProduct) return ProductType.Past
    else return ProductType.All
  }, [isAlternativeProduct, isPastProduct])

  const handleEnquireOptions = () => {
    quoteProduct.setQuoteType(QuoteModalType.ENQUIRY)
    quoteProduct.setProduct(item)
  }

  const productDetailsNavigate = useCallback(() => {
    openProductDetailsLink(item, isAllProduct, productType, navigate)
  }, [item, isAllProduct, productType])

  const cartUpdated = React.useCallback(() => {
    toast.dismiss('cart-updated')
    setTimeout(() => {
      toast('Cart updated', {
        toastId: 'cart-updated',
        type: 'warning',
        icon: <MaterialSymbol>check_circle</MaterialSymbol>,
      })
    }, 1000)
  }, [item as CartItem])

  React.useEffect(() => {
    window.addEventListener('cartUpdated', cartUpdated)
    return () => {
      window.removeEventListener('cartUpdated', cartUpdated)
    }
  }, [cartUpdated])

  const handleAddToCart = () => {
    dispatch(
      CartSlice.actions.addToCart({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        accountNumber: currentAccount!.accountNumber,
        // parseInt neccessary for typesense, returns a number rather than a string
        product: { ...item, quantity: 1, productId: parseInt(item.productId.toString()) },
      }),
    )
    cartUpdated()
  }

  const onChangeQuantity = (value: number) => {
    if (currentAccount) {
      dispatch(
        CartSlice.actions.setQuantity({
          accountNumber: currentAccount.accountNumber,
          product: item as CartItem,
          amount: value,
        }),
      )
      cartUpdated()
    }
  }

  const renderQuickOrderButton = React.useMemo(() => {
    if (canUseQuickOrder && !isDisabled && !isEndOfLife) {
      return (
        <QuickOrderButton
          item={item}
          onChangeQuantity={onChangeQuantity}
          handleAddToCart={handleAddToCart}
          handleShowOptions={productDetailsNavigate}
          handleEnquireOptions={handleEnquireOptions}
          isAlternativeProduct={isAlternativeProduct}
        />
      )
    }
  }, [canUseQuickOrder, isDisabled, isEndOfLife])

  const renderLastOrdered = React.useMemo(() => {
    if (isValidDate(lastOrderedDate)) {
      return (
        <ItemContainer>
          <ItemLabel variant='caption' align='right' sx={{ textTransform: 'uppercase' }}>
            Last ordered
          </ItemLabel>
          <ItemLabel
            variant='caption'
            align='right'
            data-testid={TestIds.feat.product.card.lastOrdered}
          >
            {dayjs(lastOrderedDate).format('DD MMM YYYY')}
          </ItemLabel>
        </ItemContainer>
      )
    }
  }, [lastOrderedDate])

  const renderSideContainer = React.useMemo(() => {
    if (!isSmall) {
      return (
        <SideContainer>
          {renderLastOrdered}
          {renderQuickOrderButton}
        </SideContainer>
      )
    }
  }, [isSmall, renderQuickOrderButton, renderLastOrdered])

  return (
    <ProductCardParent
      item={item}
      disableLink={disableLink}
      isPastProduct={isPastProduct}
      isAlternativeProduct={isAlternativeProduct}
      showRemove={showRemove}
      enableQuote={enableQuote}
      condensed={condensed}
      testId={testId}
      renderSideContainer={renderSideContainer}
      renderQuickOrderButton={renderQuickOrderButton}
    />
  )
}

export default ProductCard
